import React from 'react';
import { cn } from '@/lib/utils';
import { logoFor, logoSrc } from '@/lib/brand-logos';

/**
 * The brand tile every connector surface draws: Integrations cards and drawer,
 * Destinations cards and drawer, the forwarding grids.
 *
 * It used to hold a real logo only for Meta and Google and a coloured letter
 * tile for everyone else, on the rule "a real logo only where the official
 * path is simple enough to reproduce exactly". The owner wanted real logos
 * everywhere, so the rule became: the brand's OWN file, never a redrawing.
 * Which file, and where it came from, is lib/brand-logos and
 * public/logos/SOURCES.md.
 *
 * One tile for all of them: 36px, rounded, white (or the brand's ground when
 * its mark needs one). White stays white in dark mode on purpose - a black
 * mark like TikTok's disappears on a dark tile, and one tile that works for
 * every brand beats per-brand dark variants nobody publishes.
 *
 * `brand` is any card key; lib/brand-logos resolves it. With no logo:
 *   `icon`  a lucide icon on the neutral tile - our own pieces (routing, the
 *           partner API) and categories (another calling provider) are not
 *           brands and must not look like one
 *   `name`  letters, the last resort for a brand whose file is not in yet
 */
// `size`: md is the 36px tile for lists and drawers; lg is the 48px tile a
// gallery card leads with, where the logo IS the card's identity and at 36px
// with a 64% mark it read as an afterthought (owner review, 29 Sep 2026).
// xs/sm: the 16px and 24px marks inside table rows and the flow strip (v3).
const TILE = { xs: 'size-4 rounded-[4px]', sm: 'size-6 rounded-md', md: 'size-9 rounded-[10px]', lg: 'size-12 rounded-xl' };

export function BrandMark({ brand, icon: Icon, name, className, size = 'md' }) {
  const logo = logoFor(brand);
  const lg = size === 'lg';
  if (logo) {
    const bleed = logo.fit === 'bleed';
    return (
      <span
        className={cn(
          'grid shrink-0 place-items-center overflow-hidden border border-border', TILE[size] || TILE.md,
          !logo.bg && 'bg-white',
          className,
        )}
        style={logo.bg ? { background: logo.bg } : undefined}
      >
        {/* width/height reserve the box before the file arrives; the classes
            size it. Lazy: a page of 60 cards should not fetch 60 files before
            the first screen is drawn. */}
        <img
          src={logoSrc(logo)}
          alt=""
          aria-hidden="true"
          width={lg ? 48 : 36}
          height={lg ? 48 : 36}
          loading="lazy"
          decoding="async"
          draggable={false}
          className={bleed ? 'size-full object-cover' : lg ? 'size-[70%] object-contain' : 'size-[64%] object-contain'}
        />
      </span>
    );
  }
  if (Icon) {
    // Neutral tile with the entry's lucide icon.
    return (
      <span className={cn('grid shrink-0 place-items-center bg-primary/10 text-primary', TILE[size] || TILE.md, className)}>
        <Icon className={lg ? 'size-6' : size === 'xs' ? 'size-2.5' : size === 'sm' ? 'size-3.5' : 'size-4'} strokeWidth={lg ? 1.75 : 2} />
      </span>
    );
  }
  const letters = String(name || brand || '')
    .replace(/[^A-Za-z0-9 ]/g, '').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('');
  return (
    <span className={cn('grid shrink-0 place-items-center border border-border bg-muted/50 font-semibold text-foreground', TILE[size] || TILE.md, lg ? 'text-[15px]' : size === 'xs' ? 'text-[8px]' : size === 'sm' ? 'text-[10.5px]' : 'text-[13px]', className)}>
      {letters}
    </span>
  );
}

export default BrandMark;
